<template>
    <div class="impact-brief-config">
        <h3 class="headline">
            Configure your Onboarding Impact Brief
        </h3>
        <pendo-card
            width="800px"
            class="info__card">
            <p class="info__header">
                Improve onboarding effectiveness
            </p>
            <p class="info__text">
                If you're using Pendo to guide new users, track onboarding success, and reduce time to value, answer the
                following questions to turn your data into a shareable brief that summarizes adoption, engagement, and
                results.
            </p>
            <p class="info__text">
                <strong>Note:</strong> You’ll get more accurate onboarding insights after collecting at least 6 months
                of data. If you haven’t reached that threshold, consider revisiting this feature later.
            </p>
        </pendo-card>
        <pendo-card
            class="impact-brief-config__card"
            title="Configurations"
            width="800px">
            <pendo-form
                :model="form"
                :rules="rules"
                :validate-on-rule-change="false">
                <div class="onboarding-success-section">
                    <div class="section__title">
                        <b>What defines a successful onboarding?</b>
                    </div>
                    <pendo-form-item
                        prop="onboardingEvents"
                        label="1. What are the key events that represent successful onboarding? Select up to five key events.">
                        <event-selector
                            :selected-targets="form.onboardingEvents"
                            :target-limit-by-activity-type="{ event: 5 }"
                            :target-types-config="{ apps: false, pages: true, features: true, trackEvents: true, agentEvents: false, guides: false, productArea: false }"
                            :loading-target-types="loadingTargetTypes"
                            :is-loading="isLoading"
                            :all-apps="allApps"
                            :all-pages="allPages"
                            :all-features="allFeatures"
                            :all-track-events="allTrackEvents"
                            :core-events="allCoreEvents"
                            :recent-pages="recentPages"
                            :recent-features="recentFeatures"
                            :recent-track-events="recentTrackEvents"
                            :show-select-all="true"
                            :hide-hover-card="true"
                            trigger-cy-selector="onboarding-event-trigger"
                            @events-selected="(...args) => handleEventSelected('onboardingEvents', ...args)" />
                    </pendo-form-item>
                    <pendo-alert
                        v-if="showLimitedDataNotice"
                        type="warning"
                        closable="true"
                        class="limited-data-notice">
                        {{ getOnboardingEventNames() }} no data {{ getDateRange() }}. Updating the data range or
                        choosing another event may improve your results.
                    </pendo-alert>
                    <pendo-form-item
                        prop="segment"
                        label="2. Should these events apply to all users, or a specific segment?">
                        <segment-chooser
                            :value="form.segment"
                            :max-trigger-width="220"
                            :is-loading="isSegmentsLoading"
                            :disabled-defaults="disabledDefaultSegments"
                            :disable-compound-selection="true"
                            data-cy="segment-chooser"
                            theme="light"
                            @input="form.segment = $event" />
                    </pendo-form-item>
                    <pendo-divider
                        class="divider"
                        height="2px"
                        stroke="var(--palette-gray-40)" />
                </div>
                <div class="guides-section">
                    <div class="section__title">
                        <b>Which in-app guides were launched as part of your onboarding initiative?</b>
                    </div>
                    <pendo-form-item
                        prop="guidePolls"
                        :label="
                            `3. Select all guides that were part of your onboarding initiative.  Guides with goals give you better data.`
                        ">
                        <event-selector
                            :selected-targets="form.guidePolls"
                            :target-limit-by-activity-type="{ guide: 50 }"
                            :target-types-config="{ guides: true }"
                            :loading-target-types="loadingTargetTypes"
                            default-filter-type="guides"
                            title="Select guides"
                            action-text="Select guides"
                            :all-guides="filteredGuides"
                            :recent-guides="recentGuides"
                            :all-apps="allApps"
                            data-cy="guide-polls-trigger"
                            @events-selected="(...args) => applyTargetSelections('guidePolls', ...args)" />
                    </pendo-form-item>
                    <pendo-divider
                        class="divider"
                        height="2px"
                        stroke="var(--palette-gray-40)" />
                </div>
                <div class="additional-configuration-options-section">
                    <div class="section__title">
                        <b>How would you like to configure reporting?</b>
                    </div>
                    <pendo-form-item
                        prop="onboardingLength"
                        :label="`4. How long do you expect onboarding to take?`">
                        <pendo-multiselect
                            :options="onboardingLengthOptions"
                            :value="form.onboardingLength"
                            :max="1"
                            :placeholder="'Select onboarding length'"
                            :is-loading="isLoading"
                            trigger-cy-selector="onboarding-length-trigger"
                            @input="setOnboardingLength" />
                    </pendo-form-item>
                    <pendo-form-item prop="briefDateRange">
                        <template #label>
                            <span>5. What date range do you want to focus on?</span>
                            <pendo-icon
                                v-pendo-tooltip="{
                                    content:
                                        'The selected date range compares data to the previous period of the same length. Last year includes data from two years ago, and Last six months includes data from 12 months ago.',
                                    arrow: true,
                                    multiline: true,
                                    placement: 'right',
                                    trigger: 'hover focus',
                                }"
                                class="info-icon"
                                type="info"
                                size="16"
                                stroke-width="2.5" />
                        </template>
                        <time-series-picker
                            :time-series="form.briefDateRange"
                            :shortcuts="dateRangeOptions"
                            :range-limiter="365"
                            :max-date="maxDate"
                            :is-loading="isLoading"
                            @change="setBriefDateRange" />
                    </pendo-form-item>
                    <pendo-form-item
                        v-if="showHideConfig.accountChooser"
                        prop="measureType">
                        <template #label>
                            <span>6. What do you want to measure by?</span>
                            <pendo-icon
                                v-pendo-tooltip="{
                                    content:
                                        'Choose whether to measure by individual visitors or grouped accounts. This impacts how onboarding success is calculated and reported.',
                                    arrow: true,
                                    multiline: true,
                                    placement: 'right',
                                    trigger: 'hover focus',
                                }"
                                class="info-icon"
                                type="info"
                                size="16"
                                stroke-width="2.5" />
                        </template>
                        <pendo-radio-group
                            class="measure-type-toggle"
                            direction="horizontal"
                            :value="form.measureType"
                            name="measureType"
                            @change="setMeasureType">
                            <pendo-radio
                                value="visitors"
                                label="Visitors"
                                name="measureType" />
                            <pendo-radio
                                value="accounts"
                                label="Accounts"
                                name="measureType" />
                        </pendo-radio-group>
                    </pendo-form-item>
                </div>
            </pendo-form>
        </pendo-card>
    </div>
</template>

<script>
import {
    PendoForm,
    PendoCard,
    PendoFormItem,
    PendoMultiselect,
    PendoTooltip,
    PendoRadioGroup,
    PendoRadio,
    PendoIcon,
    PendoAlert,
    PendoDivider,
} from '@pendo/components';
import { mapGetters, mapActions, mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import EventSelector from '@/dashboards-v2/components/event-selector/event-selector.vue';
import SegmentChooser from '@/common/segment-chooser/segment-chooser.vue';
import TimeSeriesPicker from '@/common/time-series-picker/time-series-picker.vue';
import { createTimeSeriesFromDate, formatDates } from '@/common/time-series-picker/time-series-picker.util.js';
import { i18n_t } from '@utils/i18n';
import moment from 'moment-timezone';

const oneMonthOption = {
    id: 'thirty-days',
    label: '30 days',
    value: 30,
};

export default {
    name: 'ImpactBriefConfig',
    components: {
        PendoForm,
        PendoCard,
        PendoFormItem,
        PendoMultiselect,
        EventSelector,
        TimeSeriesPicker,
        PendoRadioGroup,
        PendoRadio,
        PendoIcon,
        SegmentChooser,
        PendoDivider,
        PendoAlert,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        formData: {
            type: Object,
            default: () => {},
        },
        eventsWithLimitedData: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['updateFormData', 'formValidation', 'runOnboardedVisitorAgg'],
    data () {
        const defaultTimeSeries = createTimeSeriesFromDate(formatDates({ type: 'Last 365 Days' }));
        defaultTimeSeries.config.type = 'Last 365 Days';
        defaultTimeSeries.label = 'Last 365 Days';

        return {
            form: {
                onboardingEvents: {},
                segment: null,
                guidePolls: [],
                onboardingLength: oneMonthOption,
                feedbackResponse: {
                    text: '',
                    summarizedText: '',
                    summarized: false,
                },
                briefDateRange: defaultTimeSeries,
                measureType: 'visitors',
            },
            isLoading: false,
            isSegmentsLoading: true,
            briefParameters: {},
            onboardingLengthOptions: [
                {
                    id: 'one-day',
                    label: '1 day',
                    value: 1,
                },
                {
                    id: 'seven-days',
                    label: '7 days',
                    value: 7,
                },
                {
                    id: 'fourteen-days',
                    label: '14   days',
                    value: 14,
                },
                oneMonthOption,
                {
                    id: 'sixty-days',
                    label: '60 days',
                    value: 60,
                },
                {
                    id: 'ninety-days',
                    label: '90 days',
                    value: 90,
                },
                {
                    id: 'one-hundred-eighty-days',
                    label: '180 days',
                    value: 180,
                },
            ],
            dateRangeOptions: [
                {
                    label: i18n_t('timeSeriesPicker.shortcuts.Last 30 Days'),
                    type: 'Last 30 Days',
                    value: {
                        start: -1,
                        end: -30,
                    },
                },
                {
                    label: i18n_t('timeSeriesPicker.shortcuts.Last 90 Days'),
                    type: 'Last 90 Days',
                    value: {
                        start: -1,
                        end: -90,
                    },
                },
                {
                    label: i18n_t('timeSeriesPicker.shortcuts.Last 180 Days'),
                    type: 'Last 180 Days',
                    value: {
                        start: -1,
                        end: -180,
                    },
                },
                {
                    label: 'Last 365 Days',
                    type: 'Last 365 Days',
                    value: {
                        start: -1,
                        end: -365,
                    },
                },
            ],
            rules: {
                onboardingEvents: [
                    {
                        required: true,
                        message: 'Please select an onboarding event',
                        trigger: ['event-selected'],
                    },
                    {
                        validator: this.validateOnboardingEvent,
                        trigger: ['event-selected'],
                    },
                ],
                guidePolls: [
                    {
                        required: true,
                        message: 'Please select at least one guide or nps survey',
                        trigger: ['event-selected'],
                    },
                    {
                        validator: this.validateGuidePolls,
                        trigger: ['event-selected'],
                    },
                ],
            },
        };
    },
    computed: {
        ...mapState({
            pagesFetching: (state) => state.pages.fetching,
            featuresFetching: (state) => state.features.fetching,
            trackTypesFetching: (state) => state.trackTypes.fetching,
            guidesFetching: (state) => state.guides.fetching,
        }),
        ...mapGetters({
            allApps: 'apps/analyticsAppsListWithIcons',
            allPages: 'pages/analyticsPagesListWithStars',
            allFeatures: 'features/analyticsFeaturesListWithStars',
            guidesList: 'guides/list',
            allTrackEvents: 'trackTypes/analyticsTrackTypesListWithStars',
            allCoreEvents: 'coreEvents/analyticsCoreEventsListWithStars',
            recentGuides: 'guides/recentGuidesInReporting',
            recentPages: 'pages/recentPagesInReporting',
            recentFeatures: 'features/recentFeaturesInReporting',
            recentTrackEvents: 'trackTypes/recentTrackTypesInReporting',
            hasAccountLevelAnalyticsEnabled: 'subscription/hasAccountLevelAnalyticsEnabled',
            getSegmentById: 'segments/segmentById',
            getFeatureFlag: 'subscription/getFeatureFlag',
        }),
        loadingTargetTypes () {
            return {
                pages: this.pagesFetching,
                features: this.featuresFetching,
                trackEvents: this.trackTypesFetching,
                apps: !this.allApps || this.allApps.length === 0,
                guides: this.guidesFetching,
                agentEvents: true,
                productArea: true,
            };
        },
        showAnonymous () {
            return this.getFeatureFlag('showAnonymous');
        },
        showHideConfig () {
            return {
                accountChooser: this.hasAccountLevelAnalyticsEnabled,
            };
        },
        isFormValid () {
            // Required fields
            if (isEmpty(this.form.onboardingEvents)) return false;
            if (isEmpty(this.form.guidePolls)) return false;
            if (isEmpty(this.form.onboardingLength)) return false;
            if (!this.isBriefDateRangeValid) return false;
            if (isEmpty(this.form.measureType)) return false;

            return true;
        },
        filteredGuides () {
            if (!this.guidesList.length) {
                return [];
            }

            return this.guidesList;
        },
        maxDate () {
            return moment()
                .tz(moment.tz.guess())
                .toDate();
        },
        isBriefDateRangeValid () {
            return this.form.briefDateRange && this.form.briefDateRange.spec.count >= this.form.onboardingLength.value;
        },
        disabledDefaultSegments () {
            return this.showAnonymous ? ['everyone', 'anonymousVisitorsOnly'] : null;
        },
        showLimitedDataNotice () {
            return this.eventsWithLimitedData.length > 0;
        },
    },
    watch: {
        isFormValid (valid) {
            if (valid) {
                this.prepareOnboardingConfig();
            }
            this.$emit('formValidation', valid);
        },
        form: {
            deep: true,
            handler (fields) {
                if (this.isFormValid) {
                    this.prepareOnboardingConfig();
                }
                this.$emit('updateFormData', fields);
            },
        },
    },
    async created () {
        this.isLoading = true;
        this.isSegmentsLoading = true;

        await this.loadAllSegments({ reFetch: false });

        this.dependenciesPromise = Promise.all([
            this.loadAllGuides({ reFetch: false }),
            this.loadAllMetadata({ cardinality: true }),
            this.loadAllCoreEvents({ reFetch: false }),
            this.loadAllPages({ reFetch: false }),
            this.loadAllFeatures({ reFetch: false }),
            this.loadAllTrackEvents({ reFetch: false }),
            this.loadAllApps({ reFetch: false }),
            this.loadAllStars({ reFetch: false }),
        ]);

        await this.dependenciesPromise;
        this.isLoading = false;
    },
    async mounted () {
        // Wait for dependencies to be loaded if they haven't completed yet
        if (this.isSegmentsLoading) {
            await this.loadAllSegments({ reFetch: false });
        }

        if (!isEmpty(this.formData)) {
            this.form = cloneDeep(this.formData);
        } else {
            this.form.onboardingLength = oneMonthOption;
            this.form.briefDateRange = createTimeSeriesFromDate(formatDates({ type: 'Last 365 Days' }));
            this.form.briefDateRange.config.type = 'Last 365 Days';
            this.form.briefDateRange.label = 'Last 365 Days';
            this.form.measureType = 'visitors';
            this.form.segment = this.showAnonymous
                ? this.getSegmentById('everyoneExcludingAnonymous')
                : this.getSegmentById('everyone');
        }

        this.isSegmentsLoading = false;
    },
    methods: {
        ...mapActions({
            loadAllMetadata: 'metadata/loadAll',
            loadAllTrackEvents: 'trackTypes/loadAll',
            loadAllPages: 'pages/loadAll',
            loadAllFeatures: 'features/loadAll',
            loadAllApps: 'apps/loadAll',
            loadAllCoreEvents: 'coreEvents/loadAll',
            loadAllSegments: 'segments/loadAll',
            loadAllGuides: 'guides/loadAll',
            loadAllStars: 'stars/loadAll',
        }),
        applyTargetSelections (prop, evt) {
            if (typeof evt === 'object' && Object.keys(evt).length) {
                this.form[prop] = Object.keys(evt).reduce((acc, key) => {
                    if (evt[key]) {
                        acc[key] = evt[key];
                    }

                    return acc;
                }, {});
            } else {
                this.form[prop] = evt;
            }
        },
        handleEventSelected (prop, evt) {
            this.applyTargetSelections(prop, evt);
            this.$emit('runOnboardedVisitorAgg');
        },
        validateOnboardingEvent (rule, event, callback) {
            if (event && Object.keys(event).length > 0) {
                callback();
            } else {
                callback(new Error('Please select the event that represents successful onboarding'));
            }
        },
        validateGuidePolls (rule, event, callback) {
            if (event && Object.keys(event).length > 0) {
                callback();
            } else {
                callback(new Error('Please select at least one guide or nps survey'));
            }
        },
        setOnboardingLength (val) {
            this.form.onboardingLength = val;
        },
        setBriefDateRange (val) {
            this.form.briefDateRange = val;
            this.$emit('runOnboardedVisitorAgg');
        },
        setMeasureType (val) {
            this.form.measureType = val;
        },
        prepareOnboardingConfig () {
            this.form.briefDateRange.spec.period = 'daily';
            this.briefParameters = {
                onboardingEvents: this.form.onboardingEvents,
                guidePolls: this.form.guidePolls,
                onboardingLength: this.form.onboardingLength,
                feedbackResponse: this.form.feedbackResponse,
                briefDateRange: this.form.briefDateRange,
                measureType: this.form.measureType,
                segment: this.form.segment,
            };
            this.$emit('updateFormData', this.briefParameters);
        },
        getOnboardingEventNames () {
            const numEventsSelected = Object.keys(this.form.onboardingEvents).length;
            const numEventsWithLimitedData = this.eventsWithLimitedData.length;

            if (numEventsWithLimitedData === numEventsSelected && numEventsWithLimitedData > 2) {
                return 'All events show';
            }

            switch (numEventsWithLimitedData) {
                case 0:
                    return 'No events show';
                case 1:
                    return `'${this.eventsWithLimitedData[0]}' shows`;
                default:
                    // 2-4 events
                    return this.eventsWithLimitedData.reduce((acc, name, index) => {
                        return index === numEventsWithLimitedData - 1
                            ? `${acc.slice(0, -2)} and '${name}' show`
                            : `${acc}'${name}', `;
                    }, '');
            }
        },
        getDateRange () {
            if (this.form.briefDateRange.config?.type === 'Custom') {
                return 'over the selected date range';
            }
            const count = this.form.briefDateRange.spec?.count;

            return `in the last ${count} days`;
        },
    },
};
</script>

<style lang="less" scoped>
.impact-brief-config {
    display: flex;
    flex-direction: column;
    align-items: center;

    h3.headline {
        font-weight: 600;
        font-family: 'Inter';
        margin: 40px auto;
        text-align: center;
    }

    &__card {
        margin-bottom: 40px;
    }

    :deep(.pendo-form-item__label) {
        display: flex;
        align-items: center;
        font-weight: 400;

        .info-icon {
            margin-left: 4px;
        }
    }

    :deep(.pendo-form-item__content) {
        margin: 12px 0 0 22px;
    }

    .summarize-ai-error-message {
        margin-top: 8px;
        color: var(--palette-red-60);
        font-size: 14px;
    }

    .section__title {
        font-size: 14px;
        margin-bottom: 22px;

        b {
            font-weight: 600;
        }
    }

    .divider {
        margin-bottom: 22px;
    }

    .info__header {
        font-size: 16px;
        font-weight: 600;
        line-height: 135%;
        margin-bottom: 0px;
    }

    .info__text {
        line-height: 150%;
        margin-top: 16px;
        margin-bottom: 0px;
    }

    .info__card {
        margin-bottom: 33px;
        :deep(.pendo-card__body) {
            min-height: 0px !important;
        }
    }
}

.limited-data-notice {
    margin-bottom: 16px;
    margin-left: 22px;
    width: 636px;
    background: var(--palette-yellow-20);
    border: 1px solid var(--palette-yellow-70);

    :deep(.pendo-alert__icon) {
        align-self: center;
    }
}
</style>
